
import { PropType, defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { AssociationType } from '@/types/AssociationType';
import ConfirmationOverlay from './ConfirmationOverlay.vue';
import useAssociation from '@/shared/composables/useAssociation';
import MultipleTrailerSelection from './MultipleTrailerSelection.vue';
import { ActionTypes } from '@/store/actionTypes';
import globalConstants from '@/shared/globalConstants';
import { getDirectContactTitle } from '@/shared/helpers';
import Trailer from '@/types/Trailer';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';

export default defineComponent({
    name: 'AlreadyHaveTrailerAttached',
    components: {
        ConfirmationOverlay,
        MultipleTrailerSelection,
    },
    props: {
        attachedTrailers: {
            type: Object as PropType<Array<Trailer>>,
            required: true,
        },
        promptStillNearTrailer: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['onDetach', 'onSearchForTrailers'],
    data: () => ({
        selectedTrailerIndex: 0,
        showAssessmentShaming: false,
        showDetachAllPrompt: false,
        showNearTrailer: false,
        showSelectTrailerPrompt: false,
    }),
    setup() {
        const {
            result: detachResult,
            isLoading: detachIsLoading,
            updateAssociation: detachTrailer,
            error: detachError,
        } = useAssociation();

        return {
            detachResult,
            detachIsLoading,
            detachError,
            detachTrailer,
        };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
        }),
        attachedTrailer() {
            return this.attachedTrailers?.length > 0
                ? this.selectedTrailerIndex >= 0
                    ? this.attachedTrailers[this.selectedTrailerIndex]
                    : this.attachedTrailers[0]
                : undefined;
        },
        attachedTrailerCompany() {
            return this.attachedTrailers?.length > 0
                ? this.selectedTrailerIndex >= 0
                    ? this.attachedTrailers[this.selectedTrailerIndex].company
                    : this.attachedTrailers[0].company
                : undefined;
        },
        attachedTrailerNumber() {
            return this.attachedTrailers?.length > 0
                ? this.selectedTrailerIndex >= 0
                    ? this.attachedTrailers[this.selectedTrailerIndex]
                          .trailerNumber
                    : this.attachedTrailers[0].trailerNumber
                : undefined;
        },
        detachSucceeded() {
            const isPassableReason =
                this.detachError?.reasonCode ===
                globalConstants.reasonCodes.trailerNotDetachable;
            return isPassableReason || !!this.detachResult;
        },
        subheadingMessage(): string {
            const driverContact = getDirectContactTitle(
                this.$route.params.company as string
            );
            return `Contact your ${driverContact} if this information looks wrong`;
        },
        subtitleText(): string {
            return this.attachedTrailers?.length === 1
                ? `Trailer #${this.attachedTrailers[0].trailerNumber}`
                : '';
        },
    },
    methods: {
        ...mapActions({
            setSelectedTrailer: ActionTypes.SET_SELECTED_TRAILER,
        }),
        routeToAssessment(): void {
            const trailerNumber = this.attachedTrailerNumber;
            const company = this.driver.company;

            this.setSelectedTrailer({
                trailer: this.attachedTrailer,
                trailerSelectionMethod: TrailerSelectionMethod.manual,
            });

            this.$router.push({
                name: 'TrailerAssessment',
                params: {
                    trailerNumber,
                    company,
                    fromDetachModal: 'true',
                },
                query: {
                    association: AssociationType.DETACH,
                },
            });
        },
        async removeAttachedTrailer() {
            if (this.attachedTrailerNumber && this.attachedTrailerCompany) {
                await this.detachTrailer(
                    AssociationType.DETACH,
                    this.attachedTrailerNumber,
                    this.attachedTrailerCompany,
                    this.bestAvailableLocation
                );
                if (this.detachSucceeded) {
                    this.displayDetachSuccessBanner();
                } else {
                    this.displayDetachErrorBanner();
                }
            }
            this.$emit('onDetach', this.detachSucceeded);
        },
        displayPreviousModal() {
            if (this.promptStillNearTrailer === true) {
                this.showAssessmentShaming = false;
                this.showNearTrailer = false;
            } else if (this.showSelectTrailerPrompt === true) {
                this.showSelectTrailerPrompt = false;
            } else if (this.showDetachAllPrompt === true) {
                this.showDetachAllPrompt = false;
                this.showSelectTrailerPrompt = true;
            } else this.displayNearTrailer();
        },
        displayNearTrailer() {
            this.showAssessmentShaming = false;
            this.showSelectTrailerPrompt = false;
            if (this.selectedTrailerIndex === -1) {
                this.showDetachAllPrompt = true;
            } else {
                this.showNearTrailer = true;
            }
        },
        displayAssessmentShaming() {
            this.showNearTrailer = false;
            this.showAssessmentShaming = true;
        },
        displayDetachSuccessBanner() {
            this.$store.dispatch(
                ActionTypes.SET_DETACH_SUCCESS_BANNER,
                undefined
            );
        },
        displayDetachErrorBanner() {
            this.$store.dispatch(
                ActionTypes.SET_DETACH_ERROR_BANNER,
                undefined
            );
        },
        displaySelectTrailerIfNecessary() {
            if (this.attachedTrailers.length > 1) {
                this.showSelectTrailerPrompt = true;
            } else {
                this.displayNearTrailer();
            }
        },
        searchForTrailers() {
            this.$emit('onSearchForTrailers');
        },
        selectTrailer(event: any) {
            this.selectedTrailerIndex = event.selectedTrailerIndex;
            this.displayNearTrailer();
        },
    },
});
